import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function getErrorStack(error: unknown): string | undefined {
  if (error instanceof Error) return error.stack;
  return undefined;
}

/**
 * Route-level fallback -- centered error with retry, keeps sidebar/nav working.
 */
export function RouteErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const message = getErrorMessage(error);
  const stack = getErrorStack(error);

  return (
    <div className="flex h-full min-h-[400px] items-center justify-center p-8">
      <div className="text-center max-w-md">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-vizimix-100 mb-2">
          This page crashed
        </h2>
        <p className="text-sm text-vizimix-400 mb-6">
          An unexpected error occurred while rendering this page. You can try
          again or navigate to a different section.
        </p>
        <Button variant="outline" onClick={resetError} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
        {import.meta.env.DEV && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-vizimix-500 cursor-pointer hover:text-vizimix-400">
              Error Details
            </summary>
            <pre className="mt-2 text-xs text-red-300/70 font-mono whitespace-pre-wrap overflow-auto max-h-40 bg-red-950/30 rounded p-3 border border-red-900/50">
              {message}
              {stack && `\n\n${stack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
